import React, { useState, useEffect } from "react";
import {
  Edit,
  Trash,
  ChevronLeft,
  ChevronRight,
  Search,
  UserPlus,
  Users,
  RefreshCw,
  Filter,
  Download,
  Upload,
  Shield,
  Settings
} from "lucide-react";
import * as XLSX from 'xlsx';
import AddUserModal from "../ui/Models/AddUSerModels";
import UpdateUserModal from "../ui/Models/UpdateUserModal";
import axios from "axios";
import { toast } from "react-toastify";

const UsersSection = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToUpdate, setUserToUpdate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;
  const [uploading, setUploading] = useState(false);

  // Définir l'URL de base de l'API dans une constante
  const API_BASE_URL = "https://api.greenecobagsusa.com/api";

  // Configurer axios avec le token d'authentification
  const setupAxiosAuth = () => {
    // Récupérer le token depuis le localStorage
    const token = localStorage.getItem('token');

    if (token) {
      // Configurer les en-têtes par défaut pour toutes les requêtes
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      return true;
    }
    return false;
  };
  const handleDownloadExcel = () => {
    try {
      // Préparer les données pour l'export
      const exportData = users.map(user => ({
        'ID': user.id,
        'Nom': user.name,
        'Email': user.email,
        'Téléphone': user.telephone,
        'Rôle': user.isAdmin ? 'Admin' : 'Utilisateur',
        'Date d\'inscription': new Date(user.created_at).toLocaleDateString('fr-FR')
      }));

      // Créer un nouveau classeur
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Utilisateurs");

      // Générer le fichier Excel et le télécharger
      const fileName = `utilisateurs_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      // ✅ Notification de succès
      toast.success("✅ Excel file downloaded successfully !");
    } catch (error) {
      console.error("Error while exporting:", error);
      toast.error("❌ Error while exporting data !");
    }
  };

  const handleUploadExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);

      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          // Lire la première feuille
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // Convertir en JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          // Vérifier et configurer l'authentification
          const isAuthenticated = setupAxiosAuth();
          if (!isAuthenticated) {
            alert("Non authentifié. Veuillez vous reconnecter.");
            return;
          }

          // Importer les utilisateurs un par un
          let successCount = 0;
          let errorCount = 0;

          for (const row of jsonData) {
            try {
              const userData = {
                name: row['Nom'] || row['nom'] || row['Name'],
                email: row['Email'] || row['email'],
                telephone: row['Téléphone'] || row['telephone'] || row['Telephone'],
                password: row['Mot de passe'] || row['password'] || 'password123', // Mot de passe par défaut
                isAdmin: row['Rôle'] === 'Admin' || row['Role'] === 'Admin' || false
              };

              // Validation basique
              if (!userData.name || !userData.email) {
                console.warn("Ligne ignorée (données manquantes):", row);
                errorCount++;
                continue;
              }

              await axios.post(`${API_BASE_URL}/users`, userData);
              successCount++;
            } catch (error) {
              console.error("Erreur lors de l'import d'un utilisateur:", error);
              errorCount++;
            }
          }

          // Rafraîchir la liste des utilisateurs
          await fetchUsers();

          alert(`Import terminé!\nSuccès: ${successCount}\nErreurs: ${errorCount}`);
        } catch (error) {
          console.error("Erreur lors de la lecture du fichier:", error);
          alert("Erreur lors de la lecture du fichier Excel");
        } finally {
          setUploading(false);
          // Réinitialiser l'input file
          event.target.value = '';
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Erreur lors de l'upload:", error);
      alert("Erreur lors de l'upload du fichier");
      setUploading(false);
    }
  };
  const fetchUsers = async () => {
    try {
      setLoading(true);

      // Vérifier et configurer l'authentification
      const isAuthenticated = setupAxiosAuth();

      console.log("Fetching users from:", `${API_BASE_URL}/users`);

      const response = await axios.get(`${API_BASE_URL}/users`, {
        timeout: 10000,
      });

      if (response.data) {
        setUsers(response.data.users || []);
      } else {
        setError("Réponse invalide de l'API");
      }
    } catch (err) {
      console.error("Fetch error:", err);

      if (err.code === "ECONNABORTED") {
        setError("La requête a pris trop de temps. Veuillez réessayer.");
      } else if (err.response) {
        if (err.response.status === 401) {
          setError("Non authentifié. Veuillez vous reconnecter.");
          // Rediriger vers la page de login si le token n'est plus valide
          localStorage.removeItem('token'); // Supprimer le token invalide
          // Si vous avez une fonction de redirection
          // redirectToLogin();
        } else {
          setError(`Erreur du serveur: ${err.response.data.message || err.response.statusText}`);
        }
      } else if (err.request) {
        setError("Impossible de se connecter au serveur. Vérifiez que votre backend est en cours d'exécution.");
      } else {
        setError(`Erreur: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.telephone?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handleModalToggle = () => setIsModalOpen(!isModalOpen);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleAddUser = (newUser) => {
    setUsers((prevUsers) => [...prevUsers, newUser]);
    setIsModalOpen(false);
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleEditClick = (user) => {
    setUserToUpdate({ ...user });
    setShowUpdateModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      // Vérifier et configurer l'authentification
      const isAuthenticated = setupAxiosAuth();
      if (!isAuthenticated) {
        alert("Non authentifié. Veuillez vous reconnecter.");
        setShowDeleteModal(false);
        setUserToDelete(null);
        return;
      }

      const response = await axios.delete(`${API_BASE_URL}/users/${userToDelete.id}`);

      if (response.data) {
        setUsers(users.filter((user) => user.id !== userToDelete.id));
        console.log("Utilisateur supprimé avec succès");
      } else {
        console.error("Delete error:", response.data);
        alert(response.data.message || "Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Delete request error:", error);
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token'); // Supprimer le token invalide
        alert("Non authentifié. Veuillez vous reconnecter.");
      } else {
        alert("Erreur lors de la suppression: " + (error.response?.data?.message || error.message));
      }
    }
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleUpdateUser = async () => {
    try {
      // Vérifier et configurer l'authentification
      const isAuthenticated = setupAxiosAuth();
      if (!isAuthenticated) {
        alert("Non authentifié. Veuillez vous reconnecter.");
        setShowUpdateModal(false);
        return;
      }

      const response = await axios.put(`${API_BASE_URL}/users/${userToUpdate.id}`, userToUpdate);

      if (response.data) {
        setUsers(
          users.map((user) =>
            user.id === userToUpdate.id ? userToUpdate : user
          )
        );
        setShowUpdateModal(false);
      } else {
        console.error("Update error:", response.data);
        alert(response.data.message || "Erreur lors de la mise à jour");
      }
    } catch (error) {
      console.error("Update request error:", error);
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token'); // Supprimer le token invalide
        alert("Non authentifié. Veuillez vous reconnecter.");
      } else {
        alert("Erreur lors de la mise à jour: " + (error.response?.data?.message || error.message));
      }
    }
  };

  const retryFetch = () => {
    setLoading(true);
    setError(null);
    fetchUsers();
  };

  const getPageNumbers = () => {
    const delta = 1;
    const range = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      range.unshift("...");
    }
    if (currentPage + delta < totalPages - 1) {
      range.push("...");
    }

    range.unshift(1);
    if (totalPages !== 1) {
      range.push(totalPages);
    }

    return range;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center text-red-600 mb-4">{error}</div>
        <div className="text-center">
          <button
            onClick={retryFetch}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 py-8 px-4 sm:px-6 lg:px-8">
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 right-20 w-32 h-32 bg-green-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-emerald-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-teal-200 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse animation-delay-4000"></div>
      </div>

      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/20 p-8 mb-8 lg:ml-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center">
                <Users className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">User Management</h2>
                <p className="text-gray-600">Manage your users and their permissions</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none z-10" />
                <input
                  type="text"
                  placeholder="Search for a user..."
                  className="pl-10 pr-4 py-3 w-full border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 
                  focus:border-green-500 transition-all duration-300"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>

              <button
                onClick={handleModalToggle}
                className="group relative px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
              >
                <div className="flex items-center justify-center space-x-2">
                  <UserPlus className="w-5 h-5" />
                  <span>Add User</span>
                </div>
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/20 p-16 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-3 border-b-3 border-green-500"></div>
            <p className="mt-4 text-lg text-gray-600">Loading users...</p>
          </div>
        ) : error ? (
          <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/20 p-8 mb-8 lg:ml-12">
            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-xl">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-red-800">{error}</h3>
                  <button
                    onClick={retryFetch}
                    className="mt-4 inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition duration-300"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try again
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="hidden lg:block bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/20 overflow-hidden ml-12">
              <div className="flex justify-between items-center p-6 border-b border-gray-100">
                <div className="flex items-center justify-center text-center space-x-2 group">
                  <Filter className="w-5 h-5 text-gray-500 transition-colors duration-300 group-hover:text-green-500" />
                  <span className="text-gray-600 font-medium transition-colors duration-300 group-hover:text-green-600">
                    {filteredUsers.length} Users
                  </span>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={handleDownloadExcel}
                    className="p-2 rounded-lg hover:bg-green-100 text-green-600 transition-colors duration-200 group relative"
                    title="Download in Excel"
                  >
                    <Download className="w-5 h-5" />
                    <span className="absolute hidden group-hover:block bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap">
                      Download
                    </span>
                  </button>

                  {/* <label
                    className={`p-2 rounded-lg hover:bg-blue-100 text-blue-600 transition-colors duration-200 cursor-pointer group relative ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title="Importer depuis Excel"
                  >
                    <Upload className="w-5 h-5" />
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      onChange={handleUploadExcel}
                      disabled={uploading}
                      className="hidden"
                    />
                    <span className="absolute hidden group-hover:block bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap">
                      Importer
                    </span> */}


                  {/* </label> */}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50/80">
                    <tr>
                      <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider    ">
                        Name
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Telephone
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Registration date
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td
                          colSpan="6"
                          className="px-6 py-8 text-center text-gray-500 text-sm font-medium"
                        >
                          🔍 No users found...
                        </td>
                      </tr>
                    ) : (
                      filteredUsers
                        .slice(
                          (currentPage - 1) * itemsPerPage,
                          currentPage * itemsPerPage
                        )
                        .map((user) => (
                          <tr
                            key={user.id}
                            className="hover:bg-gray-50/80 transition-colors duration-150"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-r from-green-400 to-emerald-500 
              text-white rounded-full flex items-center justify-center">
                                  {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {user.name}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500">{user.email}</div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500">
                                {user.telephone}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                <Shield className="w-3 h-3 mr-1" />
                                {user.isAdmin ? "Admin" : "Utilisateur"}
                              </span>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500">
                                {new Date(user.created_at).toLocaleDateString()}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleEditClick(user)}
                                  className="p-2 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors duration-200"
                                >
                                  <Edit className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteClick(user)}
                                  className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors duration-200"
                                >
                                  <Trash className="h-5 w-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>

                </table>
              </div>
            </div>
            {/* Mobile Card View - AJOUTEZ CE BLOC ICI, JUSTE APRÈS LA DIV DE LA TABLE */}
            <div className="lg:hidden space-y-4 px-4">
              {loading ? (
                <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg p-8 text-center">
                  <div className="inline-block animate-spin rounded-full h-10 w-10 border-t-3 border-b-3 border-green-500"></div>
                  <p className="mt-4 text-gray-600">Loading users...</p>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg p-8 text-center">
                  <p className="text-gray-500">🔍 No users found...</p>
                </div>
              ) : (
                filteredUsers
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((user) => (
                    <div
                      key={user.id}
                      className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/20 overflow-hidden"
                    >
                      <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-4">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0 h-12 w-12 bg-white/20 backdrop-blur text-white rounded-full flex items-center justify-center font-bold text-lg">
                            {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-semibold text-lg truncate">
                              {user.name}
                            </h3>
                            <p className="text-green-100 text-sm truncate">{user.email}</p>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <span className="text-gray-500 text-sm font-medium">Telephone</span>
                          <span className="text-gray-900 text-sm">{user.telephone}</span>
                        </div>

                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <span className="text-gray-500 text-sm font-medium">Role</span>
                          <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            <Shield className="w-3 h-3 mr-1" />
                            {user.isAdmin ? "Admin" : "Utilisateur"}
                          </span>
                        </div>

                        <div className="flex justify-between items-center py-2">
                          <span className="text-gray-500 text-sm font-medium">Registration date</span>
                          <span className="text-gray-900 text-sm">
                            {new Date(user.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      <div className="bg-gray-50 px-4 py-3 flex space-x-3">
                        <button
                          onClick={() => handleEditClick(user)}
                          className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClick(user)}
                          className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                        >
                          <Trash className="h-4 w-4 mr-2" />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
              )}
            </div>

            {/* Pagination */}
            {filteredUsers.length > 0 && (
              <div className="flex justify-center mt-8 px-4">
                <nav className="relative z-0 inline-flex rounded-xl shadow-sm -space-x-px overflow-hidden">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 sm:px-3 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === 1
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-500 hover:bg-gray-50"
                      }`}
                  >
                    <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
                  </button>

                  {getPageNumbers().map((pageNum, index) =>
                    pageNum === "..." ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="relative inline-flex items-center px-2 sm:px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 text-sm font-medium ${currentPage === pageNum
                            ? "z-10 bg-gradient-to-r from-green-500 to-emerald-600 border-green-500 text-white"
                            : "bg-white text-gray-500 hover:bg-gray-50"
                          }`}
                      >
                        {pageNum}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 sm:px-3 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-500 hover:bg-gray-50"
                      }`}
                  >
                    <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
                  </button>
                </nav>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              Confirm deletion
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this user? This action
              cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                DELETE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'ajout d'utilisateur */}
      <AddUserModal
        isOpen={isModalOpen}
        onClose={handleModalToggle}
        onAddUser={handleAddUser}
      />

      {/* Modal de mise à jour d'utilisateur */}
      <UpdateUserModal
        isOpen={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        onUpdateUser={handleUpdateUser}
        userToUpdate={userToUpdate}
        setUserToUpdate={setUserToUpdate}
      />
    </div>
  );
};

export default UsersSection;